import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  X, Mail, Phone, Activity, FileText, Calendar as CalIcon, Briefcase, Receipt, Upload,
  StickyNote, Loader2, Trash2, MessageSquare, ClipboardList, Plus, CheckCircle2, Circle, Clock,
  Gift, Copy, Send, Paperclip, FileSpreadsheet,
} from "lucide-react";
import { useT, useLang } from "@/lib/i18n";

export const CLIENT_STATUS_KEYS = [
  "active","waiting_on_documents","tax_return_pending","follow_up_scheduled",
  "in_progress","on_hold","completed","archived",
];
const STATUS_COLOR_MAP = {
  active: "bg-[#15803D]", waiting_on_documents: "bg-[#F97316]",
  tax_return_pending: "bg-[#C4A252]", follow_up_scheduled: "bg-[#3B82F6]",
  in_progress: "bg-[#8B5CF6]", on_hold: "bg-[#737373]",
  completed: "bg-[#0A0A0A]", archived: "bg-[#A3A3A3]",
};

const QUICK_TAG_KEYS = [
  "called","emailed","texted","met_in_person","waiting_on_docs",
  "documents_received","tax_pending","follow_up","payment_received","payment_pending",
];

const CHECKLIST_TEMPLATE_KEYS = ["bookkeeping","tax_prep","sales_tax","notary","loan_signing","itin","translation","custom"];

const STATUS_CYCLE = { missing: "pending", pending: "received", received: "missing" };

const ItemStatusPill = ({ status, onClick }) => {
  const t = useT();
  const cfg = {
    missing:  { bg: "bg-[#8B1C22]", Icon: Circle },
    pending:  { bg: "bg-[#C4A252]", Icon: Clock },
    received: { bg: "bg-[#15803D]", Icon: CheckCircle2 },
  }[status] || { bg: "bg-[#737373]", Icon: Circle };
  const { Icon } = cfg;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white ${cfg.bg} hover:opacity-90 transition`}
      title={t("admin.client.item_status_cycle_title")}
      data-testid={`checklist-item-status-${status}`}
    >
      <Icon className="w-3 h-3" /> {t(`admin.client.item_status.${status}`)}
    </button>
  );
};

const DocumentChecklistSection = ({ clientId }) => {
  const t = useT();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tpl, setTpl] = useState("tax_prep");
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState({});

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try { const { data } = await api.get(`/admin/clients/${clientId}/checklists`); setChecklists(data); }
    catch { toast.error(t("admin.client.toast.checklist_load_failed")); }
    finally { setLoading(false); }
  }, [clientId, t]);
  useEffect(() => { load(); }, [load]);

  const createChecklist = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/clients/${clientId}/checklists`, { service: tpl });
      toast.success(`${t(`admin.client.checklist_template.${tpl}`)} ${t("admin.client.toast.checklist_added")}`);
      await load();
    } catch { toast.error(t("admin.client.toast.checklist_add_failed")); }
    finally { setBusy(false); }
  };

  const cycleStatus = async (chkId, itemId, currStatus) => {
    const next = STATUS_CYCLE[currStatus] || "missing";
    try {
      await api.patch(`/admin/clients/${clientId}/checklists/${chkId}/items/${itemId}`, { status: next });
      await load();
    } catch { toast.error(t("admin.tasks.toast.update_failed")); }
  };

  const addItem = async (chkId) => {
    const label = (newItem[chkId] || "").trim();
    if (!label) return;
    try {
      await api.post(`/admin/clients/${clientId}/checklists/${chkId}/items`, { label });
      setNewItem((p) => ({ ...p, [chkId]: "" }));
      await load();
    } catch { toast.error(t("admin.client.toast.add_item_failed")); }
  };

  const deleteItem = async (chkId, itemId) => {
    try {
      await api.delete(`/admin/clients/${clientId}/checklists/${chkId}/items/${itemId}`);
      await load();
    } catch { toast.error(t("admin.toast.failed")); }
  };

  const deleteChecklist = async (chkId, title) => {
    if (!window.confirm(`${t("admin.client.confirm_delete_checklist")} "${title}"?`)) return;
    try {
      await api.delete(`/admin/clients/${clientId}/checklists/${chkId}`);
      toast.success(t("admin.client.toast.checklist_deleted"));
      await load();
    } catch { toast.error(t("admin.toast.failed")); }
  };

  return (
    <div data-testid="client-checklist-section">
      <div className="rn3-eyebrow mb-2 flex items-center gap-2"><ClipboardList className="w-3.5 h-3.5" /> {t("admin.client.checklist.heading")}</div>

      <div className="flex items-center gap-2 mb-3">
        <select value={tpl} onChange={(e) => setTpl(e.target.value)} className="rn3-input flex-1 text-xs" data-testid="checklist-template-select">
          {CHECKLIST_TEMPLATE_KEYS.map((k) => (
            <option key={k} value={k}>{t(`admin.client.checklist_template.${k}`)}</option>
          ))}
        </select>
        <button onClick={createChecklist} disabled={busy} className="rn3-btn-gold text-xs" data-testid="checklist-add-btn">
          <Plus className="w-3.5 h-3.5" /> {t("admin.tasks.add")}
        </button>
      </div>

      {loading && <div className="text-xs text-[#737373] py-2"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> {t("common.loading")}</div>}
      {!loading && checklists.length === 0 && (
        <div className="text-xs text-[#737373] italic py-3">{t("admin.client.checklist.empty")}</div>
      )}

      <div className="space-y-4">
        {checklists.map((c) => {
          const total = (c.items || []).length;
          const received = (c.items || []).filter((i) => i.status === "received").length;
          const missing = (c.items || []).filter((i) => i.status === "missing").length;
          const pct = total ? Math.round((received / total) * 100) : 0;
          return (
            <div key={c.id} className="border border-[#E5E5E5] bg-[#FDFBF7]" data-testid={`checklist-${c.id}`}>
              <div className="px-3 py-2 border-b border-[#E5E5E5] flex items-center justify-between gap-2 bg-white">
                <div>
                  <div className="font-medium text-sm" data-testid={`checklist-title-${c.id}`}>{c.title}</div>
                  <div className="text-[10px] text-[#737373] uppercase tracking-widest mt-0.5">
                    {received}/{total} {t("admin.client.received_lower")} · {missing} {t("admin.client.missing_lower")}
                  </div>
                </div>
                <button onClick={() => deleteChecklist(c.id, c.title)} className="text-[#737373] hover:text-[#8B1C22]" title={t("admin.client.delete_checklist_title")} data-testid={`checklist-delete-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="h-1 bg-[#F3EFE9]">
                <div className="h-full bg-[#15803D] transition-all" style={{ width: `${pct}%` }} />
              </div>
              <ul className="divide-y divide-[#F3EFE9]">
                {(c.items || []).map((it) => (
                  <li key={it.id} className="px-3 py-2 flex items-center justify-between gap-2 group" data-testid={`checklist-item-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${it.status === "received" ? "text-[#737373] line-through" : "text-[#0A0A0A]"}`}>{it.label}</div>
                      {it.note && <div className="text-[10px] text-[#737373] mt-0.5 truncate">{it.note}</div>}
                    </div>
                    <ItemStatusPill status={it.status} onClick={() => cycleStatus(c.id, it.id, it.status)} />
                    <button onClick={() => deleteItem(c.id, it.id)} className="opacity-0 group-hover:opacity-100 text-[#737373] hover:text-[#8B1C22] transition" title={t("admin.client.remove_item")} data-testid={`checklist-item-delete-${it.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="px-3 py-2 border-t border-[#F3EFE9] flex items-center gap-2">
                <input
                  value={newItem[c.id] || ""}
                  onChange={(e) => setNewItem((p) => ({ ...p, [c.id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") addItem(c.id); }}
                  placeholder={t("admin.client.add_custom_item")}
                  className="rn3-input flex-1 text-xs"
                  data-testid={`checklist-new-item-${c.id}`}
                />
                <button onClick={() => addItem(c.id)} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid={`checklist-add-item-${c.id}`}>
                  <Plus className="w-3 h-3" /> {t("admin.tasks.add")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ClientStatusPill = ({ status }) => {
  const t = useT();
  const color = STATUS_COLOR_MAP[status];
  if (!color) return <span className="text-xs text-[#737373]" data-testid="client-status-none">{t("admin.client.no_status")}</span>;
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest text-white ${color}`} data-testid={`client-status-${status}`}>
      {t(`admin.client.status.${status}`)}
    </span>
  );
};

const fmt = (s) => (s || "").replace("T", " ").slice(0, 16);

// Admin-only: upload a document directly into a client's portal and/or send
// them a message. Both actions fire instant Resend emails to the client.
const DOC_CATEGORIES = ["general", "tax_return", "bookkeeping", "notary", "itin", "translation", "invoice", "statement", "other"];
const SendToClientPanel = ({ clientId, clientName, onSent }) => {
  const t = useT();
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setCategory("general"); setMessage(""); };

  const uploadDoc = async () => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error(t("admin.client.send.too_large") || "File too large (max 15MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("target_user_id", clientId);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("admin.client.send.doc_sent") || "Document shared — client emailed.");
      reset();
      onSent?.();
    } catch {
      toast.error(t("admin.client.send.doc_failed") || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const sendMsg = async () => {
    const body = message.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post("/messages", { body, target_user_id: clientId, to_admin: false });
      toast.success(t("admin.client.send.msg_sent") || "Message sent — client emailed.");
      setMessage("");
      onSent?.();
    } catch {
      toast.error(t("admin.client.send.msg_failed") || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const firstName = (clientName || "").split(" ")[0] || t("admin.client.this_client");

  return (
    <div className="border border-dashed border-[#8B1C22] bg-[#FDF6F6] p-4 space-y-4" data-testid="client-send-panel">
      <div>
        <div className="rn3-eyebrow flex items-center gap-1.5 text-[#8B1C22]"><Send className="w-3 h-3" /> {t("admin.client.send.heading") || "Send To Client"}</div>
        <div className="text-[11px] text-[#737373] mt-1">{(t("admin.client.send.hint") || "Both actions email {name} instantly.").replace("{name}", firstName)}</div>
      </div>

      {/* Document upload — drops the file directly into the client's portal */}
      <div className="border border-[#E5E5E5] bg-white p-3 space-y-2">
        <div className="text-xs font-medium uppercase tracking-widest text-[#0A0A0A] flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5 text-[#8B1C22]" /> {t("admin.client.send.upload_heading") || "Upload Document For Client"}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rn3-input text-xs sm:max-w-[180px]"
            data-testid="client-doc-category"
          >
            {DOC_CATEGORIES.map((c) => (
              <option key={c} value={c}>{t(`admin.client.send.cat.${c}`) || c.replace(/_/g, " ")}</option>
            ))}
          </select>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs flex-1 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-[#0A0A0A] file:text-white file:text-xs file:uppercase file:tracking-widest hover:file:bg-[#8B1C22] file:cursor-pointer cursor-pointer"
            data-testid="client-doc-file"
          />
        </div>
        {file && (
          <div className="text-[11px] text-[#737373] truncate">
            {file.name} · {(file.size / 1024).toFixed(0)} KB
          </div>
        )}
        <div className="flex justify-end">
          <button
            onClick={uploadDoc}
            disabled={busy || !file}
            className="rn3-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="client-doc-upload-submit"
          >
            <Upload className="w-3.5 h-3.5" /> {t("admin.client.send.upload_btn") || "Share With Client"}
          </button>
        </div>
      </div>

      {/* Direct message — shows in their portal AND emails them */}
      <div className="border border-[#E5E5E5] bg-white p-3 space-y-2">
        <div className="text-xs font-medium uppercase tracking-widest text-[#0A0A0A] flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5 text-[#8B1C22]" /> {t("admin.client.send.msg_heading") || "Send Message"}
        </div>
        <textarea
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("admin.client.send.msg_placeholder") || "Type a quick message — they'll get an instant email and see it in their portal."}
          className="rn3-input w-full text-sm"
          data-testid="client-msg-input"
        />
        <div className="flex justify-end">
          <button
            onClick={sendMsg}
            disabled={busy || !message.trim()}
            className="rn3-btn-gold text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="client-msg-send"
          >
            <Send className="w-3.5 h-3.5" /> {t("admin.client.send.msg_btn") || "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin: upload a bank/card CSV to import transactions for a client.
// Backing endpoint: POST /imports/transactions (multipart). Parsed rows land
// in the "pending" queue for Layer 3 receipt matching.
const ImportCsvPanel = ({ clientId, onImported }) => {
  const t = useT();
  const [file, setFile] = useState(null);
  const [accountLabel, setAccountLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);
  const [txns, setTxns] = useState([]);
  const [showTxns, setShowTxns] = useState(false);
  const [expandedTxn, setExpandedTxn] = useState(null); // txn id whose match suggestions are open
  const [suggestions, setSuggestions] = useState({});   // {txnId: [suggestions]}
  const [autoMatching, setAutoMatching] = useState(false);
  // Layer 4: post-to-books
  const [books, setBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState("");
  const [posting, setPosting] = useState(false);

  // Autocomplete: pull unique account labels from past import batches
  const accountSuggestions = Array.from(new Set(batches.map((b) => b.account_label).filter(Boolean))).slice(0, 8);

  const loadBatches = async () => {
    try {
      const [b, x, bk] = await Promise.all([
        api.get(`/imports/batches?user_id=${clientId}`),
        api.get(`/imports/transactions?user_id=${clientId}`),
        api.get(`/imports/books?user_id=${clientId}`),
      ]);
      setBatches(b.data);
      setTxns(x.data);
      setBooks(bk.data);
      if (bk.data.length > 0 && !selectedBookId) setSelectedBookId(bk.data[0].id);
    } catch { /* noop */ }
  };
  useEffect(() => { loadBatches(); }, [clientId]);

  const doImport = async () => {
    if (!file || !accountLabel.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target_user_id", clientId);
      fd.append("account_label", accountLabel.trim());
      const { data } = await api.post("/imports/transactions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success((t("admin.import.success") || "Imported {n} transactions").replace("{n}", data.imported));
      setFile(null);
      loadBatches();
      onImported?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || (t("admin.import.failed") || "Import failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const deleteBatch = async (b) => {
    if (!window.confirm((t("admin.import.confirm_delete") || "Delete this import and all {n} transactions?").replace("{n}", b.row_count))) return;
    try {
      await api.delete(`/imports/batches/${b.id}`);
      toast.success(t("admin.import.deleted") || "Batch deleted");
      loadBatches();
      onImported?.();
    } catch {
      toast.error(t("admin.import.delete_failed") || "Delete failed");
    }
  };

  // Layer 3: QuickBooks-style "R" toggle — flips the transaction between
  // cleared / (pending or matched). We call the backend and re-fetch so the
  // running "cleared total" stays accurate.
  const toggleReconciled = async (tx) => {
    try {
      await api.post(`/imports/transactions/${tx.id}/reconcile`);
      loadBatches();
    } catch {
      toast.error(t("admin.import.toggle_failed") || "Update failed");
    }
  };

  // Load match suggestions for a specific transaction (used when the admin
  // expands a row).
  const loadSuggestions = async (txnId) => {
    try {
      const { data } = await api.get(`/imports/transactions/${txnId}/suggestions`);
      setSuggestions((s) => ({ ...s, [txnId]: data.suggestions || [] }));
    } catch {
      setSuggestions((s) => ({ ...s, [txnId]: [] }));
    }
  };

  const toggleExpand = (txnId) => {
    const next = expandedTxn === txnId ? null : txnId;
    setExpandedTxn(next);
    if (next && !suggestions[next]) loadSuggestions(next);
  };

  const confirmMatch = async (txnId, docId) => {
    try {
      await api.post(`/imports/transactions/${txnId}/match`, { doc_id: docId });
      toast.success(t("admin.import.match_confirmed") || "Match confirmed");
      setExpandedTxn(null);
      loadBatches();
      onImported?.();
    } catch {
      toast.error(t("admin.import.match_failed") || "Match failed");
    }
  };

  const runAutoMatch = async (batchId) => {
    setAutoMatching(true);
    try {
      const { data } = await api.post(`/imports/batches/${batchId}/auto-match`);
      toast.success((t("admin.import.auto_matched") || "Auto-matched {n} of {total} pending").replace("{n}", data.auto_matched).replace("{total}", data.total_pending));
      loadBatches();
      onImported?.();
    } catch {
      toast.error(t("admin.import.auto_match_failed") || "Auto-match failed");
    } finally {
      setAutoMatching(false);
    }
  };

  // Layer 4: Post cleared transactions to the ledger → they instantly appear
  // in P&L, Balance Sheet, and Cash Flow reports for the selected book.
  const postToBooks = async () => {
    if (!selectedBookId) return;
    const clearedCount = txns.filter((tx) => tx.status === "cleared").length;
    if (clearedCount === 0) {
      toast.error(t("admin.import.no_cleared") || "No cleared transactions to post");
      return;
    }
    if (!window.confirm((t("admin.import.confirm_post") || "Post {n} cleared transactions to the ledger?").replace("{n}", clearedCount))) return;
    setPosting(true);
    try {
      const { data } = await api.post("/imports/post-cleared", { user_id: clientId, book_id: selectedBookId });
      toast.success((t("admin.import.posted") || "Posted {n} to books" + (data.skipped ? " (skipped {s})" : "")).replace("{n}", data.posted).replace("{s}", data.skipped));
      loadBatches();
      onImported?.();
    } catch {
      toast.error(t("admin.import.post_failed") || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  const money = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n)).toFixed(2)}`;
  const statusColors = {
    pending: "bg-white text-[#0A0A0A] border-[#E5E5E5]",
    matched: "bg-[#FDFBF7] text-[#8B1C22] border-[#C4A252]",
    cleared: "bg-[#0A6B3A] text-white border-[#0A6B3A]",
    posted: "bg-[#0A0A0A] text-white border-[#0A0A0A]",
    ignored: "bg-[#F3EFE9] text-[#737373] border-[#E5E5E5]",
  };

  return (
    <div className="border border-dashed border-[#C4A252] bg-[#FDFBF7] p-4 space-y-4" data-testid="client-import-panel">
      <div>
        <div className="rn3-eyebrow flex items-center gap-1.5 text-[#C4A252]"><FileSpreadsheet className="w-3 h-3" /> {t("admin.import.heading") || "Import Bank/Card CSV"}</div>
        <div className="text-[11px] text-[#737373] mt-1">{t("admin.import.hint") || "Upload a CSV of transactions to reconcile against uploaded receipts."}</div>
      </div>

      {/* Upload row */}
      <div className="border border-[#E5E5E5] bg-white p-3 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#737373]">{t("admin.import.account_label") || "Account label"}</label>
            <input
              type="text"
              value={accountLabel}
              onChange={(e) => setAccountLabel(e.target.value)}
              placeholder={t("admin.import.account_placeholder") || "e.g. Chase Business Checking"}
              list="import-account-suggestions"
              className="rn3-input w-full text-xs mt-1"
              data-testid="import-csv-account"
            />
            <datalist id="import-account-suggestions">
              {accountSuggestions.map((a) => <option key={a} value={a} />)}
            </datalist>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#737373]">{t("admin.import.file_label") || "CSV file"}</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-xs file:mr-2 file:py-1 file:px-2 file:border-0 file:bg-[#0A0A0A] file:text-white file:text-[10px] file:uppercase file:tracking-widest hover:file:bg-[#8B1C22] file:cursor-pointer cursor-pointer w-full mt-1"
              data-testid="import-csv-file"
            />
          </div>
        </div>
        {file && <div className="text-[11px] text-[#737373] truncate">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
        <div className="flex justify-end">
          <button
            onClick={doImport}
            disabled={busy || !file || !accountLabel.trim()}
            className="rn3-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="import-csv-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {t("admin.import.submit") || "Import CSV"}
          </button>
        </div>
      </div>

      {/* Batches list */}
      {batches.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-[#737373]">{t("admin.import.recent_batches") || "Recent Imports"}</div>
            {batches[0] && (
              <button
                onClick={() => runAutoMatch(batches[0].id)}
                disabled={autoMatching}
                className="text-[10px] uppercase tracking-widest text-[#8B1C22] hover:text-[#6B141A] disabled:opacity-50"
                data-testid="import-automatch-btn"
              >
                {autoMatching ? <Loader2 className="w-3 h-3 inline animate-spin" /> : "⚡"} {t("admin.import.auto_match_btn") || "Auto-match latest"}
              </button>
            )}
          </div>
          {batches.slice(0, 5).map((b) => (
            <div key={b.id} className="border border-[#E5E5E5] bg-white p-2 flex items-start justify-between gap-2" data-testid={`import-batch-${b.id}`}>
              <div className="text-xs flex-1 min-w-0">
                <div className="font-medium">{b.account_label} <span className="text-[#737373] font-normal">· {b.row_count} {t("admin.import.txns") || "txns"}</span></div>
                <div className="text-[11px] text-[#737373] truncate">{b.filename} · {new Date(b.imported_at).toLocaleString()}</div>
                <div className="text-[11px] mt-0.5">
                  <span className="text-[#8B1C22]">{t("admin.import.debits") || "Debits"}: {money(-b.total_debits)}</span>
                  {" · "}
                  <span className="text-[#0A6B3A]">{t("admin.import.credits") || "Credits"}: {money(b.total_credits)}</span>
                </div>
              </div>
              <button onClick={() => deleteBatch(b)} className="text-[#8B1C22] hover:text-[#6B141A] p-1" aria-label="Delete batch" data-testid={`import-batch-delete-${b.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Transactions list */}
      {txns.length > 0 && (
        <div>
          <button
            onClick={() => setShowTxns((s) => !s)}
            className="text-xs uppercase tracking-widest text-[#8B1C22] hover:text-[#6B141A]"
            data-testid="import-txns-toggle"
          >
            {showTxns ? (t("admin.import.hide_txns") || "Hide") : (t("admin.import.show_txns") || "Show")}{" "}
            {txns.length} {t("admin.import.txns_lower") || "imported transactions"}
          </button>
          {showTxns && (() => {
            const clearedTotal = txns.filter((x) => x.status === "cleared").reduce((sum, x) => sum + Number(x.amount || 0), 0);
            const uncleared = txns.length - txns.filter((x) => x.status === "cleared").length;
            return (
              <div className="mt-2">
                {/* Reconciliation summary — mimics QuickBooks' bank rec footer */}
                <div className="border border-[#C4A252] bg-[#FDFBF7] p-2 mb-2 flex items-center justify-between text-xs" data-testid="import-recon-summary">
                  <div>
                    <span className="text-[#737373]">{t("admin.import.cleared_count") || "Cleared"}:</span>{" "}
                    <b>{txns.length - uncleared}</b>{" / "}
                    <span className="text-[#737373]">{txns.length}</span>
                  </div>
                  <div>
                    <span className="text-[#737373]">{t("admin.import.cleared_total") || "Cleared total"}:</span>{" "}
                    <b className={clearedTotal < 0 ? "text-[#8B1C22]" : "text-[#0A6B3A]"}>{money(clearedTotal)}</b>
                  </div>
                </div>

                {/* Layer 4: Post cleared to books — appears when there is at least
                    one cleared transaction and the client has ≥1 book. */}
                {books.length > 0 && txns.some((tx) => tx.status === "cleared") && (
                  <div className="border border-[#0A6B3A] bg-[#F0FAF4] p-2 mb-2 flex items-center justify-between gap-2 flex-wrap text-xs" data-testid="import-post-panel">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#0A6B3A]">{t("admin.import.post_ready") || "Ready to post to ledger"}</div>
                      <div className="text-[11px] text-[#737373]">{(t("admin.import.post_hint") || "{n} cleared txns will land in your P&L / Balance Sheet").replace("{n}", txns.filter((tx) => tx.status === "cleared").length)}</div>
                    </div>
                    <select
                      value={selectedBookId}
                      onChange={(e) => setSelectedBookId(e.target.value)}
                      className="rn3-input text-xs max-w-[180px]"
                      data-testid="import-post-book"
                    >
                      {books.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={postToBooks}
                      disabled={posting || !selectedBookId}
                      className="rn3-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="import-post-btn"
                    >
                      {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "→"} {t("admin.import.post_btn") || "Post to Books"}
                    </button>
                  </div>
                )}

                <div className="border border-[#E5E5E5] bg-white max-h-[28rem] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F3EFE9] text-[10px] uppercase tracking-widest text-[#737373] sticky top-0">
                      <tr>
                        <th className="p-2 w-8 text-center" title="Reconciled">R</th>
                        <th className="p-2 text-left">{t("admin.import.col.date") || "Date"}</th>
                        <th className="p-2 text-left">{t("admin.import.col.description") || "Description"}</th>
                        <th className="p-2 text-right">{t("admin.import.col.amount") || "Amount"}</th>
                        <th className="p-2 text-left">{t("admin.import.col.status") || "Status"}</th>
                        <th className="p-2 w-6"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {txns.slice(0, 200).map((tx) => {
                        const isCleared = tx.status === "cleared";
                        const isMatched = tx.status === "matched";
                        const sugs = suggestions[tx.id] || [];
                        return (
                          <React.Fragment key={tx.id}>
                            <tr className={`border-t border-[#E5E5E5] hover:bg-[#FDFBF7] ${isCleared ? "bg-[#F3EFE9]" : ""}`} data-testid={`import-txn-${tx.id}`}>
                              <td className="p-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => toggleReconciled(tx)}
                                  className={`w-5 h-5 border-2 flex items-center justify-center transition ${isCleared ? "bg-[#0A6B3A] border-[#0A6B3A] text-white" : "border-[#737373] text-[#737373] hover:border-[#0A6B3A] hover:text-[#0A6B3A]"}`}
                                  aria-label={isCleared ? "Un-reconcile" : "Mark reconciled"}
                                  data-testid={`import-txn-r-${tx.id}`}
                                  title="Toggle R (reconciled)"
                                >
                                  <span className="text-[10px] font-bold">R</span>
                                </button>
                              </td>
                              <td className="p-2 whitespace-nowrap">{tx.date}</td>
                              <td className="p-2 truncate max-w-[180px]" title={tx.description}>{tx.description}</td>
                              <td className={`p-2 text-right font-medium whitespace-nowrap ${tx.amount < 0 ? "text-[#8B1C22]" : "text-[#0A6B3A]"}`}>{money(tx.amount)}</td>
                              <td className="p-2"><span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${statusColors[tx.status] || statusColors.pending}`}>{tx.status}</span></td>
                              <td className="p-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(tx.id)}
                                  className="text-[#737373] hover:text-[#8B1C22] text-[10px]"
                                  aria-label="Show match suggestions"
                                  data-testid={`import-txn-suggest-${tx.id}`}
                                  title={t("admin.import.suggest_matches") || "Find receipt match"}
                                >
                                  {expandedTxn === tx.id ? "▲" : "▼"}
                                </button>
                              </td>
                            </tr>
                            {expandedTxn === tx.id && (
                              <tr className="bg-[#FAF7F0]" data-testid={`import-suggestions-${tx.id}`}>
                                <td colSpan={6} className="p-3">
                                  <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">{t("admin.import.suggested_matches") || "Suggested Receipt Matches"}</div>
                                  {sugs.length === 0 && <div className="text-[11px] text-[#737373] italic">{t("admin.import.no_matches") || "No confident matches found for this transaction."}</div>}
                                  <div className="space-y-1">
                                    {sugs.map((s) => (
                                      <div key={s.doc.id} className="border border-[#E5E5E5] bg-white p-2 flex items-center justify-between gap-2 text-[11px]" data-testid={`suggestion-${s.doc.id}`}>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className={`text-[9px] font-bold px-1.5 py-0.5 ${s.score >= 7 ? "bg-[#0A6B3A] text-white" : s.score >= 5 ? "bg-[#C4A252] text-[#0A0A0A]" : "bg-[#E5E5E5] text-[#737373]"}`}>
                                              {s.score >= 7 ? (t("admin.import.strong") || "STRONG") : s.score >= 5 ? (t("admin.import.likely") || "LIKELY") : (t("admin.import.weak") || "WEAK")}
                                            </span>
                                            <span className="font-medium truncate">{s.doc.vendor || s.doc.filename}</span>
                                          </div>
                                          <div className="text-[10px] text-[#737373] mt-0.5">
                                            {s.doc.amount != null && <span>${Number(s.doc.amount).toFixed(2)} · </span>}
                                            {s.doc.period && <span>{s.doc.period} · </span>}
                                            {s.reasons.amount_matches && <span className="text-[#0A6B3A]">✓ amount </span>}
                                            {s.reasons.account_match && <span className="text-[#0A6B3A]">✓ account </span>}
                                            {s.reasons.vendor_overlap > 0.25 && <span className="text-[#0A6B3A]">✓ vendor </span>}
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => confirmMatch(tx.id, s.doc.id)}
                                          className="text-[10px] uppercase tracking-widest bg-[#8B1C22] text-white px-2 py-1 hover:bg-[#6B141A]"
                                          data-testid={`suggestion-confirm-${s.doc.id}`}
                                        >
                                          {t("admin.import.confirm") || "Confirm"}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  {isMatched && (
                                    <button
                                      onClick={() => confirmMatch(tx.id, null)}
                                      className="text-[10px] uppercase tracking-widest text-[#8B1C22] mt-2 hover:underline"
                                      data-testid={`suggestion-unmatch-${tx.id}`}
                                    >
                                      × {t("admin.import.unmatch") || "Unlink current match"}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export const ClientDetailDrawer = ({ clientId, onClose, onUpdated }) => {
  const t = useT();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [activeTag, setActiveTag] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try { const { data } = await api.get(`/admin/clients/${clientId}`); setData(data); }
    catch { toast.error(t("admin.client.toast.load_failed")); }
    finally { setLoading(false); }
  }, [clientId, t]);

  useEffect(() => { load(); }, [load]);

  if (!clientId) return null;

  const client = data?.client;
  const counts = data?.counts || {};
  const lead = data?.lead;

  const setStatus = async (statusKey) => {
    setBusy(true);
    try {
      await api.patch(`/admin/clients/${clientId}/internal-status`, { internal_status: statusKey });
      toast.success(t("admin.toast.status_updated"));
      await load();
      onUpdated?.();
    } catch { toast.error(t("admin.client.toast.status_update_failed")); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    const body = note.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/admin/clients/${clientId}/notes`, { body, tag: activeTag || "note" });
      setNote(""); setActiveTag(null);
      toast.success(t("admin.client.toast.note_added"));
      await load();
      onUpdated?.();
    } catch { toast.error(t("admin.client.toast.note_add_failed")); }
    finally { setBusy(false); }
  };

  const quickJot = async (tag) => {
    setBusy(true);
    try {
      const body = t(`admin.client.tag_body.${tag}`);
      await api.post(`/admin/clients/${clientId}/notes`, { body, tag });
      toast.success(body);
      await load();
      onUpdated?.();
    } catch { toast.error(t("admin.toast.failed")); }
    finally { setBusy(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm(t("admin.client.confirm_delete_note"))) return;
    try {
      await api.delete(`/admin/clients/${clientId}/notes/${noteId}`);
      toast.success(t("admin.client.toast.note_deleted"));
      await load();
    } catch { toast.error(t("admin.client.toast.note_delete_failed")); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose} data-testid="client-detail-drawer">
      <div key={lang} className="bg-white w-full max-w-md h-full overflow-y-auto border-l border-[#E5E5E5]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white flex items-center justify-between z-10">
          <div>
            <div className="rn3-eyebrow">{t("admin.client.drawer_eyebrow")}</div>
            <h3 className="font-serif-display text-2xl mt-1" data-testid="client-drawer-name">
              {loading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : client?.full_name || "—"}
            </h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]" data-testid="client-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        {loading && (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C4A252]" /></div>
        )}

        {!loading && client && (
          <div className="p-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <ClientStatusPill status={client.internal_status} />
              <div className="text-xs text-[#737373]">
                {t("admin.col.joined")}: {client.created_at ? new Date(client.created_at).toLocaleDateString() : "—"}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              {client.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#737373]" /><a href={`mailto:${client.email}`} className="text-[#0A0A0A] hover:text-[#8B1C22]">{client.email}</a></div>}
              {client.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#737373]" /><a href={`tel:${client.phone}`} className="text-[#0A0A0A] hover:text-[#8B1C22]">{client.phone}</a></div>}
              {client.preferred_language && <div className="flex items-center gap-2"><Activity className="w-3.5 h-3.5 text-[#737373]" /><span className="uppercase">{client.preferred_language}</span></div>}
              {lead?.source && (
                <div className="flex items-center gap-2 text-xs text-[#737373]">
                  <span className="rn3-eyebrow">{t("admin.client.origin")}:</span> {lead.source}{lead.source_detail ? ` · ${lead.source_detail}` : ""}
                </div>
              )}
            </div>

            <div className="grid grid-cols-4 gap-2 text-center pt-2 border-t border-[#F3EFE9]">
              <div data-testid="client-count-intakes"><div className="text-lg font-serif-display">{counts.intakes ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-[#737373]"><FileText className="w-3 h-3 inline" /> {t("admin.client.count.intakes")}</div></div>
              <div data-testid="client-count-appts"><div className="text-lg font-serif-display">{counts.appointments ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-[#737373]"><CalIcon className="w-3 h-3 inline" /> {t("admin.client.count.appts")}</div></div>
              <div data-testid="client-count-docs"><div className="text-lg font-serif-display">{counts.documents ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-[#737373]"><Upload className="w-3 h-3 inline" /> {t("admin.client.count.docs")}</div></div>
              <div data-testid="client-count-books"><div className="text-lg font-serif-display">{counts.books ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-[#737373]"><Briefcase className="w-3 h-3 inline" /> {t("admin.client.count.books")}</div></div>
            </div>
            {counts.invoices > 0 && (
              <div className="text-xs text-[#737373] flex items-center gap-1.5 -mt-2"><Receipt className="w-3 h-3" /> {counts.invoices} {t("admin.client.invoices_on_file")}</div>
            )}

            <div className="border border-dashed border-[#C4A252] bg-[#FDFBF7] p-3" data-testid="client-referral-link-section">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="rn3-eyebrow flex items-center gap-1.5"><Gift className="w-3 h-3 text-[#C4A252]" /> {t("admin.client.referral_link")}</div>
                  <div className="text-xs text-[#737373] mt-1 truncate" title={`${window.location.origin}/refer?ref=${clientId}`}>
                    {window.location.origin}/refer?ref={clientId.slice(0, 8)}…
                  </div>
                </div>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/refer?ref=${clientId}`;
                    navigator.clipboard.writeText(url).then(
                      () => toast.success(t("admin.client.toast.referral_copied")),
                      () => toast.error(t("admin.client.toast.copy_failed"))
                    );
                  }}
                  className="rn3-btn-gold text-xs"
                  data-testid="client-copy-referral-link"
                >
                  <Copy className="w-3.5 h-3.5" /> {t("admin.client.copy")}
                </button>
              </div>
              <div className="text-[10px] text-[#737373] mt-2">{t("admin.client.referral_hint").replace("{name}", client.full_name?.split(" ")[0] || t("admin.client.this_client"))}</div>
            </div>

            <div>
              <div className="rn3-eyebrow mb-2">{t("admin.client.set_internal_status")}</div>
              <div className="flex flex-wrap gap-1.5">
                {CLIENT_STATUS_KEYS.map((k) => (
                  <button
                    key={k}
                    onClick={() => setStatus(k)}
                    disabled={busy}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition ${client.internal_status === k ? `${STATUS_COLOR_MAP[k]} text-white border-transparent` : "border-[#E5E5E5] text-[#404040] hover:border-[#0A0A0A]"}`}
                    data-testid={`client-set-status-${k}`}
                  >
                    {t(`admin.client.status.${k}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="rn3-eyebrow mb-2">{t("admin.client.quick_jot")}</div>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_TAG_KEYS.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => quickJot(tag)}
                    disabled={busy}
                    className="text-[11px] px-2.5 py-1 border border-[#E5E5E5] hover:border-[#8B1C22] hover:text-[#8B1C22] transition"
                    data-testid={`client-quickjot-${tag}`}
                  >
                    {t(`admin.client.tag_label.${tag}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-[#F3EFE9] pt-5">
              <DocumentChecklistSection clientId={clientId} />
            </div>

            <div className="border-t border-[#F3EFE9] pt-5">
              <SendToClientPanel
                clientId={clientId}
                clientName={client.full_name}
                onSent={() => { load(); onUpdated?.(); }}
              />
            </div>

            <div className="border-t border-[#F3EFE9] pt-5">
              <ImportCsvPanel
                clientId={clientId}
                onImported={() => { load(); onUpdated?.(); }}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="rn3-eyebrow">{t("admin.client.add_detailed_note")}</label>
                {activeTag && (
                  <span className="text-[10px] uppercase tracking-widest text-[#C4A252]">{t("admin.client.tag_label_prefix")}: {activeTag.replace(/_/g, " ")}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {QUICK_TAG_KEYS.slice(0, 6).map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag === activeTag ? null : tag)}
                    className={`text-[10px] px-1.5 py-0.5 border transition ${activeTag === tag ? "bg-[#0A0A0A] text-white border-transparent" : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A]"}`}
                    data-testid={`client-tag-${tag}`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="rn3-input w-full"
                placeholder={t("admin.client.note_placeholder")}
                data-testid="client-note-input"
              />
              <div className="mt-2 flex justify-end">
                <button onClick={addNote} disabled={busy || !note.trim()} className="rn3-btn-gold" data-testid="client-note-submit">
                  <MessageSquare className="w-4 h-4" /> {t("admin.client.append_note")}
                </button>
              </div>
            </div>

            <div>
              <div className="rn3-eyebrow mb-3 flex items-center gap-2"><StickyNote className="w-3.5 h-3.5" /> {t("admin.client.timeline")} · {(client.notes_timeline || []).length} {t("admin.client.entries")}</div>
              {(client.notes_timeline || []).length === 0 && (
                <div className="text-xs text-[#737373] italic py-3">{t("admin.client.timeline_empty")}</div>
              )}
              <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {(client.notes_timeline || []).map((e) => (
                  <li key={e.id || e.at} className="text-xs text-[#404040] border-b border-[#F3EFE9] pb-2 group" data-testid={`client-timeline-${e.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[#737373]">{fmt(e.at)}</span>
                        <span className="uppercase tracking-widest text-[10px] text-[#8B1C22]">{e.kind}</span>
                        {e.tag && <span className="uppercase tracking-widest text-[10px] text-[#C4A252]">#{e.tag}</span>}
                      </div>
                      {e.kind === "note" && e.id && (
                        <button
                          onClick={() => deleteNote(e.id)}
                          className="opacity-0 group-hover:opacity-100 text-[#737373] hover:text-[#8B1C22] transition"
                          title={t("admin.client.delete_note")}
                          data-testid={`client-delete-note-${e.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="mt-1 whitespace-pre-line">{e.body}</div>
                    {e.by && <div className="text-[10px] text-[#A3A3A3] mt-1">{t("admin.client.by")} {e.by}</div>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetailDrawer;
